import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PageHeader from '../components/PageHeader.jsx'
import { api } from '../lib/api.js'
import { useAccess } from '../lib/access.jsx'
import { ACTIONS } from '../lib/rbac.js'
import styles from './SchoolDirectory.module.css'
import { parseReviewedLoad, reviewedDryRunReady, REVIEW_PROTOCOL } from '../lib/reviewedCoachLoad.js'

/**
 * SCHOOL DIRECTORY LOAD — the button this engine never had.
 *
 * ⚠️ WHY THIS PAGE EXISTS.
 *
 * The loader has been deployed and working since July. It has already loaded 17
 * states and ~97,300 coach contacts. But there has never been anything in the
 * product that could reach it: running a state meant opening devtools on a hub
 * page and pasting a bearer token and a Google Drive file id into the console.
 * That is the "working endpoint with no button" gap this platform has shipped
 * twice before — a green backend proves nothing about whether a human can
 * actually use it.
 *
 * WHAT IT LOADS. Each state's coaches directory carries the schools AND the
 * people: coaches, athletic directors, principals, superintendents, band
 * directors — plus colours, mascot, enrolment, addresses, phones and websites.
 * The people are the point. Measured on Indiana: HubSpot holds 483 high schools
 * and exactly ONE has a single contact attached, while the file carries 16,420
 * real people whose emails overlap the CRM by zero.
 *
 * GATING. `can(SYSTEM_RUN)` decides what is DRAWN — UX only, per lib/rbac.js.
 * The enforcement is `permissions.js` on the backend, which maps every
 * `/api/coaches/` route to SYSTEM_RUN and denies anything unmapped. A user who
 * flips their role in devtools sees this page and gets a 403 from every button.
 *
 * TWO BUTTONS, ON PURPOSE. "Dry run" walks every school and reports what a real
 * run would do without writing. "Run for real" writes. They are separate
 * controls rather than a checkbox because this is a bulk write to production
 * HubSpot, and the difference should be a decision you took, not a box you left
 * in whatever state you found it.
 */

const PARENT_FOLDER = '1CArh7-biWU-JxccZoWaKSHOJtToDdkU_'
const ALL = '__ALL__'
const POLL_MS = 3000

function errText(err, fallback) {
  if (err?.missingRoute) return 'This route is not deployed on the backend yet.'
  return err?.body?.detail || err?.body?.error || err?.message || fallback
}

const num = (n) => (typeof n === 'number' ? n.toLocaleString() : '0')

export default function SchoolDirectory() {
  const { can, why } = useAccess()
  const allowed = can(ACTIONS.SYSTEM_RUN)

  const [feed, setFeed] = useState({ status: 'loading', configured: false, connected: false, error: '' })
  const [states, setStates] = useState({ status: 'loading', list: [], error: '' })
  const [pick, setPick] = useState('')
  const [props, setProps] = useState({ status: 'idle', result: null, error: '' })
  const [run, setRun] = useState({ status: 'idle', error: '' })
  const [progress, setProgress] = useState(null)
  const [showConflicts, setShowConflicts] = useState(false)
  const [showUnmatched, setShowUnmatched] = useState(false)
  /* Which directory this page is showing. See the note by the level bar. */
  const [level, setLevel] = useState('hs')
  const timer = useRef(null)
  const [reviewText, setReviewText] = useState('')
  const [capability, setCapability] = useState(false)
  const review = useMemo(() => { try { return { body: parseReviewedLoad(reviewText), error: '' } } catch (e) { return { body: null, error: reviewText ? e.message : '' } } }, [reviewText])

  /* ---- the feed, the states, and any run already in flight ---------------- */
  useEffect(() => {
    if (!allowed) return undefined
    const ac = new AbortController()
    api.googleStatus({ signal: ac.signal })
      .then((d) => setFeed({ status: 'ready', configured: !!d?.configured, connected: !!d?.connected, error: '' }))
      .catch((e) => { if (!ac.signal.aborted) setFeed({ status: 'error', configured: false, connected: false, error: errText(e, 'Could not reach the Drive feed.') }) })
    api.coachesStates({ signal: ac.signal })
      .then((d) => setStates({ status: 'ready', list: Array.isArray(d?.states) ? d.states : [], error: '' }))
      .catch((e) => { if (!ac.signal.aborted) setStates({ status: 'error', list: [], error: errText(e, 'Could not list the states.') }) })
    return () => ac.abort()
  }, [allowed])

  const poll = useCallback(() => {
    api.coachesLoadProgress()
      .then((p) => {
        setProgress(p && !p.note ? p : null)
        if (p?.finishedAt || p?.blocked || p?.stale) setRun(r => ({ ...r, status: 'done' }))
        if (!p?.running && timer.current) { clearInterval(timer.current); timer.current = null }
      })
      .catch(() => { /* a failed poll is not a failed run — leave the last good reading up */ })
  }, [])

  useEffect(() => {
    if (!allowed) return undefined
    // Never poll on mount: an older backend may resume a legacy job on GET.
    api.coachesLoadCapabilities().then(d => setCapability(d?.protocol === REVIEW_PROTOCOL)).catch(() => setCapability(false))
    return () => { if (timer.current) { clearInterval(timer.current); timer.current = null } }
  }, [allowed, poll])

  const startPolling = useCallback(() => {
    if (timer.current) clearInterval(timer.current)
    timer.current = setInterval(poll, POLL_MS)
    poll()
  }, [poll])

  /* ---- actions ------------------------------------------------------------ */
  const ensureProps = async () => {
    setProps({ status: 'working', result: null, error: '' })
    try { setProps({ status: 'done', result: await api.coachesEnsureProperties({}), error: '' }) }
    catch (e) { setProps({ status: 'error', result: null, error: errText(e, 'Could not create the properties.') }) }
  }

  const start = async (dryRun) => {
    const chosen = states.list.find((s) => s.fileId === pick)
    if (reviewText && (!capability || !review.body || (!dryRun && !reviewedDryRunReady(progress, review.body)))) return
    if (!reviewText && (pick === ALL || /^virginia/i.test(chosen?.name || ''))) { setRun({ status: 'error', error: 'Virginia requires its reviewed HS-only manifest. The all-state path is disabled to avoid including it.' }); return }
    const body = review.body ? { ...review.body, dryRun } : pick === ALL
      ? { parentFolderId: PARENT_FOLDER, create: true, enrich: true, dryRun }
      : { states: [{ fileId: chosen.fileId, name: chosen.name }], create: true, enrich: true, dryRun }
    setRun({ status: 'working', error: '' })
    try {
      await api.coachesLoadRun(body)
      setRun({ status: 'running', error: '' })
      startPolling()
    } catch (e) { setRun({ status: 'error', error: errText(e, 'Could not start the load.') }) }
  }

  const stop = async () => {
    try { await api.coachesLoadStop(); poll() }
    catch (e) { setRun((r) => ({ ...r, error: errText(e, 'Could not stop the load.') })) }
  }

  /* A state folder with no sheet is shown and disabled rather than hidden —
   * "the folder exists and is empty" is the answer somebody needs. California
   * and Washington are both in exactly that state. */
  const selectable = useMemo(() => states.list.filter((s) => s.fileId), [states.list])
  const empties = useMemo(() => states.list.filter((s) => !s.fileId), [states.list])
  const running = Boolean(progress?.running)
  const canStart = (reviewText ? capability && Boolean(review.body) : Boolean(pick) && pick !== ALL && !/^virginia/i.test(states.list.find(s => s.fileId === pick)?.name || '')) && !running && !['working', 'running'].includes(run.status)
  const canApply = canStart && (!reviewText || reviewedDryRunReady(progress, review.body))
  const t = progress?.totals || {}

  if (!allowed) {
    return (
      <>
        <PageHeader eyebrow="Data" title="School Directory Load" />
        <div className={`card ${styles.section}`}>
          <p>{why?.(ACTIONS.SYSTEM_RUN) || 'You do not have permission to run system jobs.'}</p>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader
        eyebrow="Data"
        title={level === 'college' ? 'College Directory Load' : 'High School Directory Load'}
        subtitle={level === 'college'
          ? 'The college equivalent of the state loader — colleges and the people who run their programs.'
          : "Load a state's schools and the people who run them — coaches, athletic directors, principals — from the Drive directories into HubSpot."}
      />

      {/* ⚠️ HIGH SCHOOL AND COLLEGE ARE DIFFERENT DIRECTORIES.
        * Brian: "Governing bodies and school directors should be broken up into
        * hs and college."
        *
        * This loader has only ever read the state HIGH-SCHOOL coaching
        * directories, but nothing on the page said so — so it read as "the
        * school directory" and quietly implied colleges were covered. Naming
        * the level is most of the fix. */}
      <div className={styles.levelBar}>
        <button type="button" className={styles.levelBtn} data-on={level === 'hs' ? '1' : '0'}
          onClick={() => setLevel('hs')}>High school</button>
        <button type="button" className={styles.levelBtn} data-on={level === 'college' ? '1' : '0'}
          onClick={() => setLevel('college')}>College</button>
      </div>

      {level === 'college' && (
        <section className={`card ${styles.section}`}>
          <h2 className={styles.h}>Not sourced yet — and that is the honest state</h2>
          <p className={styles.muted}>
            The loader above reads the state high-school coaching directories Alex and Brian
            built in Drive. There is no equivalent feed for colleges yet, so there is nothing
            to load — and a button here that did nothing would be worse than this sentence.
          </p>
          <p className={styles.muted}>
            <b>What it needs:</b> a Drive directory of colleges and their staff in the same
            shape as the state sheets — one row per program, with the head coach, athletic
            director and their contact details. The backend loader is level-agnostic: it reads
            a sheet, normalizes it and writes HubSpot companies and contacts. Point it at a
            college sheet and this tab works with no new code.
          </p>
          <p className={styles.muted}>
            The COLLEGE CHAMPIONSHIPS are already covered separately — see Governing Bodies →
            College, and the Championship Integrity board. This gap is the PEOPLE, not the
            titles.
          </p>
        </section>
      )}

      {level === 'hs' && (<>

      {/* ---- the feed ---- */}
      <section className={`card ${styles.section}`}>
        <div className={styles.step}><span className={styles.num}>1</span><h2 className={styles.h}>The Drive feed</h2></div>
        {feed.status === 'loading' && <p className={styles.muted}>Checking…</p>}
        {feed.status === 'error' && <p className={styles.bad}>{feed.error}</p>}
        {feed.status === 'ready' && feed.connected && (
          <p className={styles.good}>Connected. The backend reads each state sheet directly — no file data passes through this browser.</p>
        )}
        {feed.status === 'ready' && !feed.connected && (
          <div>
            <p className={styles.bad}>
              {feed.configured
                ? 'Google is configured but nobody has authorised an account. That is one click.'
                : 'The backend has no Google credentials set (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET on Railway).'}
            </p>
            {feed.configured && (
              <button className="btn" type="button"
                onClick={() => api.googleConnectUrl().then((d) => { if (d?.url) window.location.href = d.url })}>
                Connect Google Drive
              </button>
            )}
          </div>
        )}
      </section>

      {/* ---- properties ---- */}
      <section className={`card ${styles.section}`}>
        <div className={styles.step}><span className={styles.num}>2</span><h2 className={styles.h}>The fields these files need</h2></div>
        <p className={styles.muted}>
          Three fields in every directory have no home in HubSpot yet — athletic website (on ~97% of
          schools), girls athletic phone, and athletic district alignment. Without them that data is
          dropped on every run. This adds the properties and nothing else; it is safe to press twice.
        </p>
        <button className="btn" type="button" onClick={ensureProps} disabled={props.status === 'working'}>
          {props.status === 'working' ? 'Creating…' : 'Create the missing fields'}
        </button>
        {props.status === 'error' && <p className={styles.bad}>{props.error}</p>}
        {props.status === 'done' && props.result && (
          <ul className={styles.list}>
            {props.result.created?.length > 0 && <li className={styles.good}>Created: {props.result.created.join(', ')}</li>}
            {props.result.alreadyPresent?.length > 0 && <li className={styles.muted}>Already there: {props.result.alreadyPresent.join(', ')}</li>}
            {props.result.failed?.length > 0 && props.result.failed.map((f) => (
              <li key={f.property} className={styles.bad}>{f.property}: {f.error}</li>
            ))}
            {!props.result.created?.length && !props.result.failed?.length && !props.result.alreadyPresent?.length && (
              <li className={styles.muted}>Nothing to do.</li>
            )}
          </ul>
        )}
      </section>

      {/* ---- pick a state ---- */}
      <section className={`card ${styles.section}`}>
        <div className={styles.step}><span className={styles.num}>3</span><h2 className={styles.h}>Which state</h2></div>
        {states.status === 'loading' && <p className={styles.muted}>Reading the Drive tree…</p>}
        {states.status === 'error' && <p className={styles.bad}>{states.error}</p>}
        {states.status === 'ready' && (
          <>
            <p className={styles.muted}>
              A sheet whose rows are a different state is refused by the loader rather than written —
              the Oklahoma file currently contains Florida schools, so picking it writes nothing.
            </p>
            <select className={styles.picker} value={pick} onChange={(e) => setPick(e.target.value)} disabled={running}>
              <option value="">Choose a state…</option>
              <option value={ALL} disabled>All states unavailable: Virginia needs a reviewed manifest</option>
              {selectable.map((s) => <option key={s.fileId} value={s.fileId}>{s.name}</option>)}
            </select>
            {empties.length > 0 && (
              <p className={styles.muted}>
                No file yet: {empties.map((s) => s.name).join(', ')} — the folder exists but is empty.
              </p>
            )}
          </>
        )}
      </section>

      <section className={`card ${styles.section}`}>
        <h2 className={styles.h}>Reviewed Virginia high-school batch</h2>
        <p className={styles.muted}>Paste the reviewed request JSON. This uses existing schools only, excludes middle schools and held records, and does not send outreach. A completed matching dry run is required before applying.</p>
        <label>Open reviewed JSON file<input type="file" accept="application/json,.json" disabled={running} onChange={async e => {
          const file = e.target.files?.[0]
          if (!file) return
          if (file.size > 7 * 1024 * 1024) { setRun({ status: 'error', error: 'Reviewed file exceeds the 7 MB limit.' }); return }
          try { setReviewText(await file.text()); setProgress(null) } catch (error) { setRun({ status: 'error', error: error.message }) }
        }} /></label>
        <label>Reviewed request JSON<textarea className={styles.reviewInput} aria-label="Reviewed request JSON" rows={6} value={reviewText} disabled={running} onChange={e => { setReviewText(e.target.value); setProgress(null) }} /></label>
        {!capability && <p className={styles.bad}>Reviewed loader capability is not verified on the deployed backend. Virginia execution is blocked.</p>}
        {review.error && <p className={styles.bad}>{review.error}</p>}
        {review.body && <p>{review.body.strictScope.schools.length} reviewed schools. Review: {review.body.strictScope.reviewId}</p>}
      </section>
      {/* ---- run ---- */}
      <section className={`card ${styles.section}`}>
        <div className={styles.step}><span className={styles.num}>4</span><h2 className={styles.h}>Run it</h2></div>
        <p className={styles.muted}>
          The reviewed Virginia batch attaches directory contacts to reviewed existing high schools only.
          Other state runs retain their established behavior. Existing populated values are preserved.
          Check dry-run totals and exceptions before applying the approved batch.
        </p>
        <div className={styles.actions}>
          <button className="btn" type="button" disabled={!canStart} onClick={() => start(true)}>
            Dry run — write nothing
          </button>
          <button className="btn btn-primary" type="button" disabled={!canApply} onClick={() => start(false)}>
            Run for real — write to HubSpot
          </button>
          {running && <button className="btn" type="button" onClick={stop}>Stop</button>}
        </div>
        {run.status === 'error' && <p className={styles.bad}>{run.error}</p>}
        {review.body && progress?.finishedAt && !reviewedDryRunReady(progress, review.body) && progress.opts?.dryRun && <p className={styles.bad}>Dry-run outcome does not match the reviewed batch. Applying remains blocked; review the failures and held schools.</p>}
      </section>

      {/* ---- progress ---- */}
      {progress && (
        <section className={`card ${styles.section}`}>
          <h2 className={styles.h}>
            {running ? 'Running' : 'Last run'}
            {progress.opts?.dryRun && <span className={styles.tag}>dry run — nothing written</span>}
          </h2>
          <div className={styles.grid}>
            <div><b data-zero={!t.matched ? '1' : '0'}>{num(t.matched)}</b><span>matched</span></div>
            <div><b data-zero={!t.created ? '1' : '0'}>{num(t.created)}</b><span>schools created</span></div>
            <div><b data-zero={!t.createdHs ? '1' : '0'}>{num(t.createdHs)}</b><span>· high schools</span></div>
            <div><b data-zero={!t.createdMs ? '1' : '0'}>{num(t.createdMs)}</b><span>· middle schools</span></div>
            <div><b data-zero={!t.contacts ? '1' : '0'}>{num(t.contacts)}</b><span>people attached</span></div>
            <div><b data-zero={!t.filled ? '1' : '0'}>{num(t.filled)}</b><span>empty fields filled</span></div>
            <div><b data-zero={!t.conflicts ? '1' : '0'}>{num(t.conflicts)}</b><span>conflicts to review</span></div>
            {/* This IS the Drive-vs-HubSpot difference, so it says so rather
                than making somebody infer it from the word "unmatched". */}
            <div><b data-zero={!t.unmatched ? '1' : '0'}>{num(t.unmatched)}</b><span>in Drive, not in HubSpot</span></div>
            <div><b>{num(t.heldSchools)}</b><span>schools held outside this batch</span></div>
            <div><b data-zero={!t.failed ? '1' : '0'}>{num(t.failed)}</b><span>failed</span></div>
          </div>
          {/* Only states that actually did something. A column of "Oregon: 0"
              is noise that buries the states that ran. */}
          <div className={styles.states}>
            {(progress.states || []).filter((s) => s.done || s.total).map((s) => {
              const pct = s.total ? Math.round((s.done / s.total) * 100) : 0
              return (
                <div key={s.fileId} className={styles.stateRow}>
                  <div className={styles.stateTop}>
                    <b>{s.name}</b>
                    <span>{num(s.done)}{s.total ? ` / ${num(s.total)}` : ''}</span>
                  </div>
                  <div className={styles.bar} data-done={s.total && s.done >= s.total ? '1' : '0'}>
                    <i style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
          {(progress.states || []).some((s) => !s.done && !s.total) && (
            <p className={styles.muted}>
              {(progress.states || []).filter((s) => !s.done && !s.total).length} more states queued and not started.
            </p>
          )}
          {/* A run that died mid-way (a deploy will do it) leaves `running` set
              forever. The backend decides staleness from how long ago the run
              last moved and says so, rather than reporting a six-week-old job
              as live — and rather than leaving the start buttons disabled. */}
          {progress.stale && <p className={styles.bad}>{progress.staleNote}</p>}

          {/* ⚠️ The sheet said it was a different state, so it was skipped.
              This is real: the Oklahoma file contains Florida schools. */}
          {progress.mismatched?.length > 0 && (
            <ul className={styles.list}>
              {progress.mismatched.map((m, i) => (
                <li key={i} className={styles.bad}>
                  <b>{m.expected}</b> was skipped — the sheet contains {m.found.join(', ')}. Nothing was written for it.
                </li>
              ))}
            </ul>
          )}

          {progress.lastError && <p className={styles.bad}>Last error — {progress.lastError}</p>}

          {progress.conflictSample?.length > 0 && (
            <div>
              <button className="btn" type="button" onClick={() => setShowConflicts((v) => !v)}>
                {showConflicts ? 'Hide' : 'Show'} conflicts ({progress.conflictSample.length})
              </button>
              {showConflicts && (
                <ul className={styles.list}>
                  {progress.conflictSample.map((c, i) => (
                    <li key={i}><b>{c.school}</b> — {c.property}: HubSpot has “{c.existing}”, the file says “{c.incoming}”</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {progress.unmatchedSample?.length > 0 && (
            <div>
              <button className="btn" type="button" onClick={() => setShowUnmatched((v) => !v)}>
                {showUnmatched ? 'Hide' : 'Show'} unmatched ({progress.unmatchedSample.length})
              </button>
              {showUnmatched && (
                <ul className={styles.list}>
                  {progress.unmatchedSample.map((u, i) => (
                    <li key={i}>{u.state} — {u.name} <span className={styles.muted}>({u.band === 'ms' ? 'middle' : 'high'})</span></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}
      </>)}
    </>
  )
}
